import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Badge, Dropdown, Spinner, Form } from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import Utility from '../../../../GlobalModules/Utility/Utility';
import { toast } from "react-toastify";
import { getStockTransferDetails } from "../Controller/StockTransferController";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faEdit, faTrash } from "@fortawesome/free-solid-svg-icons";
import ConfirmationAlert from "../../../../GlobalModules/Utility/Components/ConfirmationAlert";
import ModalGlobal from "../../../../GlobalModules/Utility/Components/ModalGlobal";
import ErrorBoundary from "../../../../GlobalModules/Utility/Components/ErrorBoundary";
import VerifyProductForm from "../Components/VerifyProductForm";
import { mergeProduct, rejectProduct } from "../Controller/ProductController";
import { loaderStateTrue, loaderStateFalse, handleActiveLink } from '../../../../Actions/AllAction';

import { connect } from "react-redux";
import { withNamespaces } from "react-i18next";

const VerifyStockTransfer = (props) => {
    const { slack } = useParams();
    const [stockTransfer, setStockTransfer] = useState(null);
    const [productTransferSlack, setProductTransferSlack] = useState(null);
    const [modalVisible, setModalVisible] = useState(false);
    const [selectedProductData, setSelectedProductData] = useState(null);
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [pendingRejectSlack, setPendingRejectSlack] = useState(null);
    const [showAcceptConfirmation, setShowAcceptConfirmation] = useState(false);
    const [pendingAcceptData, setPendingAcceptData] = useState(null);

    const fetchStockTransfer = async () => {
        try {
            const response = await getStockTransferDetails(slack);
            if (response.success && response.data) {
                setStockTransfer(response.data);
            } else {
                toast.error('Stock Transfer not found');
            }
        } catch (error) {
            console.error('Error fetching stock transfer:', error);
            toast.error('Failed to fetch stock transfer details');
        }
    };

    // Fetch stock transfer data based on ID
    useEffect(() => {
        fetchStockTransfer();
    }, [slack]);

    // submit product merge
    const handleVerifyProduct = async (data) => {
        props.loaderStateTrue();
        try {
            const response = await mergeProduct({
                stock_transfer_slack: productTransferSlack,
                store_product_slack: data?.product?.value || "",
                accepted_quantity: data.quantity.toFixed(2),
                store_id: localStorage.getItem("store_id")
            })

            if (response.success) {
                // Show success message
                Utility.toastNotifications(response?.message || 'Product merged successfully', 'Success', 'success');
                
                // Close modal
                setModalVisible(false);
                
                // Reset states
                setProductTransferSlack(null);
                setSelectedProductData(null);
                
                // Refresh data
                await fetchStockTransfer();
            } else {
                Utility.toastNotifications(response?.message || 'Failed to merge product', 'Error', 'error');
            }
        } catch (e) {
            setModalVisible(false);
            setProductTransferSlack(null);
            setSelectedProductData(null);

            console.error('Error verifying stock transfer:', e);
            Utility.toastNotifications('An error occurred while merging a product', 'Error', 'error');
        } finally {
            props.loaderStateFalse();
        }
    }

    const confirmationModalForReject = (slack) => {
        setPendingRejectSlack(slack)
        setShowRejectModal(true)
    }

    const closeRejectModal = () => {
        setShowRejectModal(false)
        setPendingRejectSlack(null)
    }

    const confirmRejectModal = async () => {
        closeRejectModal()
        await handleRejectProduct(pendingRejectSlack)
    }

    // reject product transfer
    const handleRejectProduct = async (slack) => {
        try {
            const response = await rejectProduct(slack);

            if (response.success) {
                Utility.toastNotifications('Product rejected successfully', 'Success', 'success');
                await fetchStockTransfer();
            } else {
                Utility.toastNotifications(response?.message || 'Failed to reject product', 'Error', 'error');
            }
        } catch (e) {
            console.error('Error rejecting product:', e);
            Utility.toastNotifications('An error occurred while rejecting a product', 'Error', 'error');
        }
    }

    const handleModalCancel = () => {
        if (!props.loaderState) {
            setModalVisible(false);
            setProductTransferSlack(null);
            setSelectedProductData(null);
        }
    }

    const closeAcceptConfirmation = () => {
        setShowAcceptConfirmation(false);
        setPendingAcceptData(null);
    }

    const confirmAcceptProduct = async () => {
        const data = pendingAcceptData;
        setShowAcceptConfirmation(false);
        // console.log("confirmAcceptProduct", data);
        props.loaderStateTrue();
        try {
            const response = await mergeProduct({
                stock_transfer_slack: data.productTransferSlack,
                store_product_slack: data.toStoreProductSlack,
                accepted_quantity: parseFloat(data.quantity).toFixed(2),
                store_id: localStorage.getItem("store_id")
            })

            if (response.success) {
                Utility.toastNotifications(response?.message || 'Product merged successfully', 'Success', 'success');
                await fetchStockTransfer();
            } else {
                Utility.toastNotifications(response?.message || 'Failed to merge product', 'Error', 'error');
            }
        } catch (e) {
            console.error('Error verifying stock transfer:', e);
            Utility.toastNotifications('An error occurred while merging a product', 'Error', 'error');
        } finally {
            props.loaderStateFalse();
            setPendingAcceptData(null);
        }
    }

    if (!stockTransfer) {
        return (
            <Container fluid className="p-0">
                <div className="text-center py-5">
                    <p>Loading stock transfer details...</p>
                </div>
            </Container>
        );
    }

    const toStoreId = stockTransfer.StockTransfer_ToStore?.id;
    const currentStoreId = localStorage.getItem('store_id');

    //======= add action permission ====//
    // Check multiple permissions at once
    const stockTransferVerifyAcceptPermissions = Utility.hasMultipleActionPermissions(
        'stock',
        'stock_transfer',
        ['view', 'create', 'edit', 'delete', 'export', 'approve', 'receive', 'verify', 'reject', 'verify_accept'],
        props?.userCredentials?.user_details?.user_type || ['admin', 'app_admin']
    );

    return (
        <>
            <Container fluid className="p-0">
                <Row className="mb-0">
                    <Col>
                    </Col>
                    <Col xs="auto" className="d-flex align-items-center">
                        <Link to={`/${localStorage.getItem('i18nextLng') || 'en'}/stock/transfer`} className="btn btn-outline-secondary me-2">
                            <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                            Back to List
                        </Link>
                    </Col>
                </Row>

                <Row>
                    <Col lg={12} className="mb-4">
                        <Card className="custom-border-light h-100">
                            <Card.Body>
                                <Row className={'mb-4'}>
                                    <Col md={11}>
                                        <h2 className="text-title">Verify Stock
                                            Transfer <b>#{stockTransfer.stock_transfer_reference}</b></h2>
                                    </Col>
                                    <Col md={1}>
                                        {stockTransfer.status == 0 && <Badge
                                            bg={'secondary'}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            Pending
                                        </Badge>}
                                        {stockTransfer.status == 1 && <Badge
                                            bg={'info'}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            Initiated
                                        </Badge>}
                                        {stockTransfer.status == 2 && <Badge
                                            bg={'success'}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            Verified
                                        </Badge>}
                                    </Col>
                                </Row>

                                <Row className={'mb-4'}>
                                    <Col md={3}>
                                        <p className="mb-2 fw-medium">Source Store</p>
                                        <p className="text-muted mb-0">{stockTransfer.from_store_name}</p>
                                    </Col>
                                    <Col md={3}>
                                        <p className="mb-2 fw-medium">Destination Store</p>
                                        <p className="text-muted mb-0">{stockTransfer.to_store_name}</p>
                                    </Col>
                                    <Col md={3}>
                                        <p className="mb-2 fw-medium">Created By</p>
                                        <p className="text-muted mb-0">{stockTransfer.StockTransfer_CreatedUser.first_name} {stockTransfer.StockTransfer_CreatedUser.last_name}</p>
                                    </Col>
                                    <Col md={3}>
                                        <p className="mb-2 fw-medium">Updated By</p>
                                        <p className="text-muted mb-0">{stockTransfer.StockTransfer_UpdatedUser.first_name} {stockTransfer.StockTransfer_UpdatedUser.last_name}</p>
                                    </Col>
                                </Row>

                                <Row className={'mb-4'}>
                                    <Col md={3}>
                                        <p className="mb-2 fw-medium">Created On</p>
                                        <p className="text-muted mb-0">{Utility.get12HourFormat(stockTransfer.createdAt) || '-'}</p>
                                    </Col>
                                    <Col md={3}>
                                        <p className="mb-2 fw-medium">Updated On</p>
                                        <p className="text-muted mb-0">{Utility.get12HourFormat(stockTransfer.updatedAt) || '-'}</p>
                                    </Col>
                                </Row>

                                <h2 className="text-headline">Product Information</h2>

                                <div className="mt-4">
                                    {stockTransfer.transfer_products.map((el, i) => (
                                        <Row key={el.id} className="mt-4 align-items-end">
                                            <Col md={2}>
                                                <Form.Group>
                                                    <Form.Label>Source Store Product Code</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        value={el.product_code}
                                                        disabled={true}
                                                    />
                                                </Form.Group>
                                            </Col>

                                            <Col md={4}>
                                                <Form.Group>
                                                    <Form.Label>Name & Description</Form.Label>
                                                    <div className="d-flex align-items-center">
                                                        <Form.Control type="text" value={el.product_name} disabled />
                                                        {!el.to_store_product && (
                                                            <Badge bg="success" className="ms-2">New</Badge>
                                                        )}
                                                    </div>
                                                </Form.Group>
                                            </Col>

                                            <Col md={3}>
                                                <Form.Group>
                                                    <Form.Label>Transferred Quantity</Form.Label>
                                                    <Form.Control
                                                        type="number"
                                                        inputMode="decimal"
                                                        step="0.01"
                                                        min="0.01"
                                                        disabled={true}
                                                        defaultValue={el.quantity ?? ''} // keep empty string when not
                                                        placeholder="e.g. 1.25"
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={3}>
                                                {el.status === 0 ? <>
                                                    {currentStoreId == toStoreId &&
                                                        <>
                                                            {stockTransferVerifyAcceptPermissions.verify_accept && (
                                                                <Button
                                                                    variant="outline-primary"
                                                                    onClick={() => {
                                                                        if (el.to_store_product) {
                                                                            // If to_store_product exists, show confirmation alert
                                                                            setPendingAcceptData({
                                                                                productTransferSlack: el.slack,
                                                                                toStoreProductSlack: el.to_store_product.slack,
                                                                                quantity: el.quantity,
                                                                                product_name: el.product_name
                                                                            });
                                                                            setShowAcceptConfirmation(true);
                                                                        } else {
                                                                            // If to_store_product doesn't exist, open the product selection modal
                                                                            setProductTransferSlack(el.slack);
                                                                            setSelectedProductData({
                                                                                quantity: el.quantity,
                                                                                product_name: el.product_name,
                                                                                product_code: el.product_code
                                                                            });
                                                                            setModalVisible(true);
                                                                        }
                                                                    }}
                                                                    disabled={props.loaderState}
                                                                >
                                                                    Verify & Accept
                                                                </Button>
                                                            )}
                                                            {stockTransferVerifyAcceptPermissions.reject && (
                                                                <Button
                                                                    className={'ms-2'}
                                                                    variant="danger"
                                                                    onClick={(e) => {
                                                                        e.preventDefault()
                                                                        e.stopPropagation()
                                                                        confirmationModalForReject(el.slack)
                                                                    }}
                                                                    disabled={props.loaderState}
                                                                >
                                                                    Reject
                                                                </Button>
                                                            )}
                                                        </>
                                                    }
                                                </> : <Badge
                                                    bg={el.status_data?.value_constant === 'REJECTED' ? 'danger' : 'success'}
                                                    className={el.status_data?.color || ''}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    {el.status_data?.label || 'Accepted'}
                                                </Badge>}
                                            </Col>
                                        </Row>
                                    ))}
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>

            <ModalGlobal
                show={modalVisible}
                onHide={handleModalCancel}
                className="modalcustomize confirmationalertmodal"
                bodyClassName="cancelConfirmationbody"
                headerclassName="close_btn_icon"
                title="Verify product"
                footer={false}
                backdrop={props.loaderState ? 'static' : true}
                keyboard={!props.loaderState}
                body={
                    <ConfirmationAlert
                        BodyFirstContent={
                            <VerifyProductForm
                                onSubmit={handleVerifyProduct}
                                onCancel={handleModalCancel}
                                initialQuantity={selectedProductData?.quantity}
                                productData={selectedProductData}
                                loading={props.loaderState}
                            />
                        }
                        BodySecondContent=""
                        BodyThirdContent=""
                        confirmationButtonContent='confirm'
                        cancelButtonContent='cancel'
                        saveShow={false}
                        cancelShow={false}
                    />
                }
            />

            <ErrorBoundary title="ModalGlobal Error">
                <ModalGlobal
                    show={showRejectModal}
                    onHide={closeRejectModal}
                    className="modalcustomize confirmationalertmodalDialog"
                    bodyClassName="cancelConfirmationbody"
                    headerclassName="close_btn_icon"
                    title="Confirm Reject Product"
                    footer={false}
                    body={
                        <>
                            <ErrorBoundary title="ConfirmationAlert Error">
                                <ConfirmationAlert
                                    BodyFirstContent="Are you sure you want to reject this product?"
                                    BodySecondContent=""
                                    BodyThirdContent=""
                                    confirmationButtonContent="Yes"
                                    cancelButtonContent="No"
                                    deleteConfirmButton={confirmRejectModal}
                                    deleteCancleButton={closeRejectModal}
                                />
                            </ErrorBoundary>
                        </>
                    }
                />
            </ErrorBoundary>

            <ErrorBoundary title="ModalGlobal Error">
                <ModalGlobal
                    show={showAcceptConfirmation}
                    onHide={closeAcceptConfirmation}
                    className="modalcustomize confirmationalertmodalDialog"
                    bodyClassName="cancelConfirmationbody"
                    headerclassName="close_btn_icon"
                    title="Confirm Accept Product"
                    footer={false}
                    body={
                        <>
                            <ErrorBoundary title="ConfirmationAlert Error">
                                <ConfirmationAlert
                                    BodyFirstContent={`Are you sure you want to accept and merge "${pendingAcceptData?.product_name}" with quantity ${pendingAcceptData?.quantity}?`}
                                    BodySecondContent="This will use the existing destination product."
                                    BodyThirdContent=""
                                    confirmationButtonContent="Yes"
                                    cancelButtonContent="No"
                                    deleteConfirmButton={confirmAcceptProduct}
                                    deleteCancleButton={closeAcceptConfirmation}
                                />
                            </ErrorBoundary>
                        </>
                    }
                />
            </ErrorBoundary>
        </>
    );
};

const mapStateToProps = (globalState) => ({
    userCredentials: globalState.LoginReducer.userCredentials,
    userEntryType: globalState.LoginReducer.userEntryType,
    currentStore: globalState.StoreReducer.currentStore,
    loaderState: globalState.mainReducerData.loaderState,
});

export default connect(mapStateToProps, { 
    loaderStateTrue, 
    loaderStateFalse, 
    handleActiveLink 
})(withNamespaces()(VerifyStockTransfer));
